import client from '@lib/sanity';
import Img, { ImageProps } from 'next/image';
import { ImageUrlBuilder, useNextSanityImage } from 'next-sanity-image';
import React, { useState, useRef, useEffect } from 'react';

interface IImage {
  sanityImage: {
    asset: {
      _ref: string,
      _type: string,
    },
    crop?: {
      _type: string,
      top: number,
      bottom: number,
      left: number,
      right: number,
    },
    hotspot?: {
      _type: string,
      x: number,
      y: number,
      height: number,
      width: number,
    },
    _type: string,
    alt: string,
  }
  className?: string,
  layout?: 'intrinsic' | 'responsive' | 'fill' | 'fixed',
  priority?: ImageProps['priority'],
  objectFit?: 'fill' | 'contain' | 'cover' | 'none' | 'scale-down',
  width?: ImageProps['width'],
  height?: ImageProps['height'],
  sizes?: ImageProps['sizes'],
}

const styling = {
  image: 'w-full h-auto',
};

const defaultMaxWidth = 1440;
const defaultMaxHeight = 960;
const imageQuality = 85;

const customImageBuilder = (
  imageUrlBuilder: ImageUrlBuilder,
  options: any,
  elementWidth: number = defaultMaxWidth,
  elementHeight: number = defaultMaxHeight,
  objectFit: string|undefined,
  layout: string|undefined
) => {
  try {
    const ratio = options.croppedImageDimensions.aspectRatio || 1;
    const neededWidth = objectFit === 'contain' ? 0 : elementHeight * ratio;
    const calculatedWidth = Math.round(Math.max(elementWidth, neededWidth));

    // Ensure width is a valid integer and within bounds
    options.width = Math.min(
      Math.floor(options.croppedImageDimensions.width || defaultMaxWidth),
      calculatedWidth,
      defaultMaxWidth
    );

    if (layout === 'fill') {
      // For carousel/fill layouts, use a larger width to maintain quality
      return imageUrlBuilder
        .width(defaultMaxWidth)  // Fixed width for better quality
        .quality(imageQuality)
        .fit('clip');
    }

    return imageUrlBuilder
      .width(Math.max(1, Math.round(options.width)))
      .quality(imageQuality)
      .fit('clip');
  } catch (error) {
    console.error('Error in customImageBuilder:', error);
    return imageUrlBuilder
      .width(2048)
      .quality(imageQuality)
      .fit('clip');
  }
};

export const Image = ({
  sanityImage,
  width,
  height,
  layout = 'intrinsic',
  objectFit = 'cover',
  className = '',
  priority = false,
  sizes,
}: IImage) => {
  const ref = useRef<HTMLDivElement>(null);
  const [ elementWidth, setElementWidth ] = useState(width ? Math.round(Number(width)) : defaultMaxWidth);
  const [ elementHeight, setElementHeight ] = useState(height ? Math.round(Number(height)) : defaultMaxHeight);

  useEffect(() => {
    // Only update dimensions if they're smaller than current
    const imgElement = (ref.current?.firstChild as HTMLElement);
    if (imgElement) {
      const newWidth = imgElement.offsetWidth;
      const newHeight = imgElement.offsetHeight;

      if (newWidth && newWidth < elementWidth) {
        setElementWidth(Math.round(newWidth));
      }
      if (newHeight && newHeight < elementHeight) {
        setElementHeight(Math.round(newHeight));
      }
    }
  }, [ elementWidth, elementHeight ]);

  // Get image props with dynamic sizing
  const imageProps = useNextSanityImage(
    client,
    sanityImage,
    {
      imageBuilder: (imageBuilder, options) => {
        return customImageBuilder(imageBuilder, options, elementWidth, elementHeight, objectFit, layout);
      },
    }
  );

  // Handle hotspot positioning and get Sanity props
  const getSanityProps = () => {
    let style = { objectPosition: 'center' };
    if (sanityImage?.hotspot && objectFit !== 'contain') {
      const { x = 0.5, y = 0.5 } = sanityImage.hotspot;
      const left = x * 100;
      const top = y * 100;
      style = { objectPosition: `${ left }% ${ top }%` };
    }

    const { width: imgWidth, height: imgHeight, src } = imageProps;

    // Ensure width and height are valid integers
    const safeWidth = typeof imgWidth === 'number' ? Math.max(1, Math.round(imgWidth)) : defaultMaxWidth;
    const safeHeight = typeof imgHeight === 'number' ? Math.max(1, Math.round(imgHeight)) : defaultMaxHeight;

    const baseProps = {
      style,
      src,
      width: safeWidth,
      height: safeHeight,
    };

    if (layout === 'fill') {
      return {
        ...baseProps,
        width: undefined,
        height: undefined,
      };
    }

    return baseProps;
  };

  const loader = ({ src, width, height, quality }: {
    src: string,
    width?: number | string,
    height?: number | string,
    quality?: number | string
  }): string => {
    try {
      const url = new URL(src);
      // Convert all parameters to valid integers
      const safeWidth = width ? Math.max(1, Math.round(Number(width))) : undefined;
      const safeHeight = height ? Math.max(1, Math.round(Number(height))) : undefined;
      const safeQuality = quality ? Math.min(100, Math.max(1, Math.round(Number(quality)))) : undefined;

      if (safeWidth && !isNaN(safeWidth)) {
        url.searchParams.set('w', safeWidth.toString());
        // Add blur effect for small images (thumbnails/placeholders)
        if (safeWidth <= 40) {
          url.searchParams.set('blur', '20');
          url.searchParams.set('q', '30');
        }
      }
      if (safeHeight && !isNaN(safeHeight)) {
        url.searchParams.set('h', safeHeight.toString());
      }
      if (safeQuality && !isNaN(safeQuality)) {
        url.searchParams.set('q', safeQuality.toString());
      }

      const finalUrl = url.toString();
      return finalUrl;
    } catch (error) {
      console.error('Error in loader:', error, { src, width, height, quality });
      return src;
    }
  };

  // Get wrapper class based on layout
  const wrapperClass = layout === 'fill'
    ? 'relative w-full h-full'
    : layout === 'responsive'
      ? 'block'
      : '';

  if (!sanityImage?.asset?._ref) {
    return (
      <div
        className={ `${ wrapperClass } bg-gray-200 min-h-[200px]` }
        role='img'
        aria-label={ sanityImage?.alt || 'Missing image' }
      />
    );
  }

  return (
    <div ref={ ref } className={ wrapperClass }>
      <Img
        { ...getSanityProps() }
        alt={ sanityImage?.alt || 'Missing image' }
        className={ `${ styling.image } ${ className } object-${ objectFit }` }
        fill={ layout === 'fill' }
        priority={ priority }
        sizes={ sizes || `(max-width: 768px) 100vw,
          (max-width: 1200px) 50vw,
          ${ defaultMaxWidth }px` }
        loader={ loader }
        unoptimized={ true } // This is already optimized by Sanity
      />
    </div>
  );
};

export default Image;
